import { getJobRequirementRecruitmentAgents } from "@endpoints/hiringCompany/requirements";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { colors } from "@utils/theme";
// @ts-ignore
import ProfileImg from "@assets/images/profilepic.png";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

type RequirementRecruitmentAgent = {
  _id: string;
  recruiterId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  profilePic?: string;
  refNo?: string;
  userName?: string;
  companyName?: string;
  companyRefNo?: string;
  grantAccessAt?: string;
};

const RecruitmentAgents: React.FC = () => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [agents, setAgents] = useState<RequirementRecruitmentAgent[]>([]);

  useEffect(() => {
    const fetchAgents = async () => {
      if (!id) {
        setIsLoading(false);
        return;
      }
      try {
        const { success, data } = await getJobRequirementRecruitmentAgents(id);
        if (success && Array.isArray(data)) {
          setAgents(data);
        } else {
          setAgents([]);
        }
      } catch (error) {
        setAgents([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgents();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col rounded-lg bg-white w-full flex-grow p-6 justify-center items-center">
        <LoadingAnimation color={colors.PrimaryBlack} />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 p-4 md:p-8 max-w-6xl mx-auto w-full">
      <h3 className="text-sm font-semibold text-primary mb-4">Recruitment agents</h3>

      {agents.length === 0 ? (
        <div className="text-xs text-gray-500 border border-dashed border-gray-300 rounded-md p-4">
          No recruitment agents are assigned to this job requirement.
        </div>
      ) : (
        <div className="overflow-x-auto w-full">
          <table className="w-full table-auto text-sm">
            <thead className="border-b border-[#E0E0E0]">
              <tr>
                <th className="py-3 text-left px-2 text-xs font-semibold text-light-black w-[30%] whitespace-nowrap">
                  Agent
                </th>
                <th className="py-3 text-left px-2 text-xs font-semibold text-light-black whitespace-nowrap">
                  Reference
                </th>
                <th className="py-3 text-left px-2 text-xs font-semibold text-light-black w-[25%] whitespace-nowrap">
                  Company
                </th>
                <th className="py-3 text-left px-2 text-xs font-semibold text-light-black w-[30%] whitespace-nowrap">
                  Email
                </th>
                <th className="py-3 text-left px-2 text-xs font-semibold text-light-black whitespace-nowrap">
                  Access granted on
                </th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => (
                <tr key={agent._id} className="border-b">
                  <td className="px-2 py-3">
                    <div className="flex items-center gap-2">
                      <img
                        src={agent.profilePic || ProfileImg}
                        alt={`${agent.firstName || ""} ${agent.lastName || ""}`.trim()}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      <span className="font-medium text-xs text-[#6F6F6F] whitespace-nowrap">
                        {`${agent.firstName || ""} ${agent.lastName || ""}`.trim() ||
                          agent.userName ||
                          "-"}
                      </span>
                    </div>
                  </td>
                  <td className="px-2 py-3 font-medium text-xs text-[#6F6F6F] whitespace-nowrap">
                    {agent.refNo || "-"}
                  </td>
                  <td className="px-2 py-3 font-medium text-xs text-[#6F6F6F]">
                    {agent.companyName
                      ? `${agent.companyName}${agent.companyRefNo ? ` (${agent.companyRefNo})` : ""}`
                      : "-"}
                  </td>
                  <td className="px-2 py-3 font-medium text-xs text-[#6F6F6F] whitespace-nowrap">
                    {agent.email || "-"}
                  </td>
                  <td className="px-2 py-3 font-medium text-xs text-[#6F6F6F] whitespace-nowrap">
                    {agent.grantAccessAt
                      ? dayjs(agent.grantAccessAt).format("DD MMM YYYY")
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RecruitmentAgents;
